import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dagn7f9tto0ceqqni0n0";

type Status = { type: "idle" | "success" | "error"; msg: string };

export default function Contact() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });
  const [sending, setSending] = useState(false);

  const channels = [
    { icon: "ri-mail-line", title: "官方邮箱", desc: "liuyan@meiribaoliao.example" },
    { icon: "ri-chat-1-line", title: "私信投稿", desc: "面向表单与社交私信通道提供线索" },
    { icon: "ri-checkbox-circle-line", title: "编号反馈", desc: "处理后按线索编号回传进展" },
  ];

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return;
    const formEl = e.currentTarget;
    const data = new FormData(formEl);

    // 反垃圾蜜罐校验：命中则同样给出通用成功提示，但不提交
    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus({ type: "success", msg: "线索已收到，编辑部会尽快核实处理。" });
      formEl.reset();
      return;
    }

    const message = String(data.get("message") ?? "").trim();
    if (message.length > 500) {
      setStatus({ type: "error", msg: "爆料内容请控制在 500 字以内。" });
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (value && value !== "" && key !== "website_alt") {
        payload.append(key, String(value));
      }
    });

    setSending(true);
    setStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.code ||
        responseText;

      const serverIsSpam =
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("spam");

      if (res.ok && !serverIsSpam && parsed?.code === "OK") {
        setStatus({ type: "success", msg: "线索已收到，编辑部会尽快核实处理。" });
        formEl.reset();
      } else {
        setStatus({
          type: "error",
          msg:
            typeof serverMsg === "string" && serverMsg
              ? serverMsg
              : "提交失败，请稍后再试或通过邮箱直接联系。",
        });
      }
    } catch {
      setStatus({ type: "error", msg: "网络异常，请稍后再试或通过邮箱直接联系。" });
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="contact" className="w-full bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              Report a Tip
            </p>
            <h3 className="font-heading mt-2 text-2xl font-bold text-foreground-950 md:text-3xl">
              提供线索 · 联系投稿
            </h3>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">
              你有独家内幕或曝光素材？欢迎通过下方表单提交。请尽量说明事件经过与发生时间、地点，并附上可核实的证据，我们会第一时间跟进。
            </p>

            <div className="mt-7 space-y-4">
              {channels.map((c) => (
                <div
                  key={c.title}
                  className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h4 className="text-[15px] font-semibold text-foreground-950">{c.title}</h4>
                    <p className="mt-0.5 text-sm text-foreground-600">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h4 className="text-lg font-bold text-foreground-950">填写爆料表单</h4>
            <p className="mt-1 text-sm text-foreground-600">
              标注 <span className="text-primary-600">*</span> 为必填项，请如实填写。
            </p>

            {status.type !== "idle" && (
              <div
                role="status"
                className={`mt-4 flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${
                  status.type === "success"
                    ? "border-primary-200 bg-primary-50 text-primary-800"
                    : "border-accent-300 bg-accent-50 text-accent-900"
                }`}
              >
                <i
                  className={
                    status.type === "success" ? "ri-checkbox-circle-line mt-0.5" : "ri-error-warning-line mt-0.5"
                  }
                ></i>
                <span>{status.msg}</span>
              </div>
            )}

            <form
              className="mt-5 space-y-4"
              onSubmit={handleSubmit}
              data-readdy-form
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-sm font-medium text-foreground-800">
                    您的称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="如何称呼您"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="type" className="text-sm font-medium text-foreground-800">
                    线索类型 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="type"
                    name="type"
                    required
                    defaultValue="消费维权"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option>消费维权</option>
                    <option>民生关注</option>
                    <option>职场观察</option>
                    <option>出行交通</option>
                    <option>食品安全</option>
                    <option>其他</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact" className="text-sm font-medium text-foreground-800">
                  联系方式 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="contact"
                  name="contact"
                  type="text"
                  required
                  autoComplete="off"
                  placeholder="邮箱或手机号，便于核实反馈"
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="text-sm font-medium text-foreground-800">
                  爆料内容 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请说明事件发生的时间、地点与经过，可附上关键事实描述（500 字以内）"
                  className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              {/* 反垃圾蜜罐字段 */}
              <input
                className="antispam-guard"
                type="text"
                name="website_alt"
                id="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={sending}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-[15px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className="ri-send-plane-line"></i>
                {sending ? "正在提交…" : "提交线索"}
              </button>
              <p className="text-center text-xs text-foreground-500">
                提交即表示同意我们按隐私原则处理线索，身份信息严格保密。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}